import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowLeft,
  BookOpen,
  RefreshCw,
  Loader2,
  Check,
  X,
  AlertCircle,
  AlertTriangle,
  FileText,
  ShieldCheck,
  Archive,
} from 'lucide-react';
import { toast } from 'sonner';

import ThemeToggle from '@/components/ThemeToggle';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/lib/AuthContext';
import { BADGE_TOPICS } from '@/data/badges';

const topicLabel = (key) =>
  BADGE_TOPICS.find((t) => t.key === key)?.label ||
  key?.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase()) ||
  '—';

function QuestionCard({ row, flagged, busy, onApprove, onReject, onKeep }) {
  const q = row.payload || {};

  return (
    <div
      className={cn(
        'rounded-2xl border p-5',
        flagged ? 'border-destructive/40 bg-destructive/5' : 'border-border bg-card',
      )}
    >
      {flagged && row.review_note && (
        <div className="flex items-start gap-2 mb-3 pb-3 border-b border-destructive/20">
          <AlertTriangle className="w-4 h-4 mt-0.5 text-destructive flex-shrink-0" />
          <p className="text-xs text-destructive leading-relaxed">{row.review_note}</p>
        </div>
      )}

      <div className="flex items-start justify-between gap-4 mb-3">
        <div className="min-w-0">
          <p className="text-xs text-muted-foreground mb-1">
            <span className="font-medium text-foreground">{row.source_ref}</span>
            {' · '}
            {topicLabel(row.topic)}
            {row.source_version ? ` · page v${row.source_version}` : ''}
          </p>
          <p className="text-sm font-medium text-foreground leading-snug">{q.question}</p>
        </div>
        <span className="flex-shrink-0 text-[10px] font-semibold uppercase tracking-wide px-1.5 py-0.5 rounded bg-muted text-muted-foreground">
          {row.type === 'true_false' ? 'True / False' : 'Choice'}
        </span>
      </div>

      {row.type === 'multiple_choice' && Array.isArray(q.options) && (
        <ul className="space-y-1.5 mb-3">
          {q.options.map((opt, i) => (
            <li
              key={i}
              className={cn(
                'text-sm px-3 py-1.5 rounded-lg border',
                i === q.correctIndex
                  ? 'border-primary/40 bg-primary/5 text-foreground font-medium'
                  : 'border-border text-muted-foreground',
              )}
            >
              {i === q.correctIndex && (
                <Check className="inline w-3.5 h-3.5 mr-1.5 text-primary" />
              )}
              {opt}
            </li>
          ))}
        </ul>
      )}

      {row.type === 'true_false' && (
        <p className="text-sm mb-3">
          <span className="text-muted-foreground">Correct answer: </span>
          <span className="font-medium text-foreground">
            {q.correctAnswer ? 'True' : 'False'}
          </span>
        </p>
      )}

      <p className="text-xs text-muted-foreground italic border-t border-border pt-3 mb-4">
        {q.explanation}
      </p>

      <div className="flex flex-wrap gap-2">
        {flagged ? (
          <>
            <Button size="sm" className="rounded-full" disabled={busy} onClick={() => onKeep(row.id)}>
              <ShieldCheck className="w-3.5 h-3.5 mr-1.5" />
              Still correct
            </Button>
            <Button
              size="sm"
              variant="outline"
              className="rounded-full border-border"
              disabled={busy}
              onClick={() => onReject(row.id)}
            >
              <Archive className="w-3.5 h-3.5 mr-1.5" />
              Retire it
            </Button>
          </>
        ) : (
          <>
            <Button size="sm" className="rounded-full" disabled={busy} onClick={() => onApprove(row.id)}>
              <Check className="w-3.5 h-3.5 mr-1.5" />
              Approve
            </Button>
            <Button
              size="sm"
              variant="outline"
              className="rounded-full border-border"
              disabled={busy}
              onClick={() => onReject(row.id)}
            >
              <X className="w-3.5 h-3.5 mr-1.5" />
              Reject
            </Button>
          </>
        )}
      </div>
    </div>
  );
}

export default function KbTests() {
  const { isAdmin } = useAuth();

  const [drafts, setDrafts] = useState(null);
  const [flagged, setFlagged] = useState([]);
  const [preview, setPreview] = useState(null);
  const [running, setRunning] = useState(null);
  const [pageBusy, setPageBusy] = useState(null);
  const [busyId, setBusyId] = useState(null);
  // Which page row is asking "are you sure?"
  const [confirmPage, setConfirmPage] = useState(null);

  const loadQuestions = useCallback(async () => {
    const { data, error } = await supabase
      .from('questions')
      .select(
        'id, topic, type, payload, source_ref, source_version, status, needs_review, review_note, created_at',
      )
      .eq('source', 'confluence')
      .neq('status', 'archived')
      .order('source_ref', { ascending: true });

    if (error) {
      console.error('Loading questions failed:', error.message);
      setDrafts([]);
      setFlagged([]);
      return;
    }

    setDrafts((data || []).filter((q) => q.status === 'draft' && !q.needs_review));
    setFlagged((data || []).filter((q) => q.needs_review));
  }, []);

  useEffect(() => {
    loadQuestions();
  }, [loadQuestions]);

  const callFunction = async (mode, titles) => {
    const key = titles ? `${mode}:${titles[0]}` : mode;
    if (titles) setPageBusy(key);
    else setRunning(mode);

    const { data, error } = await supabase.functions.invoke('sync-confluence', {
      body: titles ? { mode, titles } : { mode },
    });

    setRunning(null);
    setPageBusy(null);

    if (error || data?.error) {
      toast.error('That failed', { description: error?.message || data.error });
      return null;
    }

    if (mode === 'preview') {
      setPreview(data);
      const stale = (data.pages || []).filter((p) => p.stale).length;
      toast.success(`${data.page_count} pages found`, {
        description: stale ? `${stale} have changed since their questions were written.` : undefined,
      });
    } else if (mode === 'recheck') {
      toast.success(
        data.flagged ? `${data.flagged} questions flagged` : 'Everything still checks out',
        { description: data.message },
      );
      await loadQuestions();
      await callFunction('preview');
    } else {
      toast.success(`${data.drafts_created ?? 0} drafts created`, { description: data.message });
      await loadQuestions();
      await callFunction('preview');
    }
    return data;
  };

  const approve = async (id) => {
    setBusyId(id);
    const { error } = await supabase.rpc('publish_questions', { p_ids: [id] });
    setBusyId(null);
    if (error) return toast.error('Could not publish', { description: error.message });
    toast.success('Published.');
    setDrafts((d) => d.filter((x) => x.id !== id));
  };

  const keep = async (id) => {
    setBusyId(id);
    const { error } = await supabase.rpc('clear_question_flags', { p_ids: [id] });
    setBusyId(null);
    if (error) return toast.error('Could not clear the flag', { description: error.message });
    setFlagged((f) => f.filter((x) => x.id !== id));
    await loadQuestions();
  };

  const retire = async (id) => {
    setBusyId(id);
    const { error } = await supabase.rpc('archive_questions', {
      p_ids: [id],
      p_note: 'Retired by an admin during review.',
    });
    setBusyId(null);
    if (error) return toast.error('Could not retire it', { description: error.message });
    setFlagged((f) => f.filter((x) => x.id !== id));
    setDrafts((d) => d.filter((x) => x.id !== id));
  };

  // Archive every question from one page. Archived, never deleted: the record
  // of what the knowledge base used to say is worth keeping, and a page pulled
  // from training today may come back next quarter.
  const archivePage = async (title) => {
    setPageBusy(`archive:${title}`);

    const { data: rows, error: readError } = await supabase
      .from('questions')
      .select('id')
      .eq('source', 'confluence')
      .eq('source_ref', title)
      .neq('status', 'archived');

    if (readError) {
      setPageBusy(null);
      return toast.error('Could not read that page', { description: readError.message });
    }

    if (!rows?.length) {
      setPageBusy(null);
      setConfirmPage(null);
      return toast.info('Nothing to archive for that page.');
    }

    const { error } = await supabase.rpc('archive_questions', {
      p_ids: rows.map((r) => r.id),
      p_note: `Page removed from training by an admin.`,
    });

    setPageBusy(null);
    setConfirmPage(null);

    if (error) return toast.error('Could not archive', { description: error.message });

    toast.success(`${rows.length} questions archived`, {
      description: 'Agents will no longer see them. Regenerate to bring the page back.',
    });

    await loadQuestions();
    await callFunction('preview');
  };

  if (!isAdmin) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center px-6">
        <div className="text-center">
          <p className="text-muted-foreground">This screen is for administrators.</p>
          <Link to="/" className="mt-4 inline-block text-primary hover:underline">
            Back to training
          </Link>
        </div>
      </div>
    );
  }

  const staleCount = (preview?.pages || []).filter((p) => p.stale).length;

  return (
    <div className="min-h-screen bg-background">
      <header className="border-b border-border bg-card/80 backdrop-blur-sm sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-6 h-14 flex items-center justify-between">
          <Link
            to="/"
            className="inline-flex items-center text-sm text-muted-foreground hover:text-foreground"
          >
            <ArrowLeft className="mr-1.5 w-4 h-4" />
            Back to quiz
          </Link>
          <div className="flex items-center gap-2">
            <span className="text-sm font-semibold tracking-tight text-foreground">KB Review</span>
            <ThemeToggle />
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-6 py-12 sm:py-16">
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-primary/10 text-primary mb-5">
            <BookOpen className="w-8 h-8" strokeWidth={1.5} />
          </div>
          <h1 className="text-2xl sm:text-3xl font-semibold tracking-tight text-foreground">
            Knowledge base review
          </h1>
          <p className="mt-2 text-muted-foreground">
            Questions drafted from Confluence pages labelled{' '}
            <code className="text-xs px-1.5 py-0.5 rounded bg-muted">training</code>. Nothing
            reaches agents until you approve it.
          </p>
        </div>

        <div className="flex flex-wrap justify-center gap-3 mb-3">
          <Button
            variant="outline"
            className="rounded-full border-border"
            disabled={running !== null}
            onClick={() => callFunction('preview')}
          >
            {running === 'preview' ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <FileText className="w-4 h-4 mr-2" />
            )}
            Preview pages
          </Button>

          <Button
            className="rounded-full"
            disabled={running !== null}
            onClick={() => callFunction('generate')}
          >
            {running === 'generate' ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <RefreshCw className="w-4 h-4 mr-2" />
            )}
            Generate drafts
          </Button>

          {staleCount > 0 && (
            <Button
              variant="outline"
              className="rounded-full border-destructive/40 text-destructive hover:bg-destructive/10"
              disabled={running !== null}
              onClick={() => callFunction('recheck')}
            >
              {running === 'recheck' ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <ShieldCheck className="w-4 h-4 mr-2" />
              )}
              Re-check {staleCount} changed {staleCount === 1 ? 'page' : 'pages'}
            </Button>
          )}
        </div>

        <p className="text-xs text-center text-muted-foreground mb-10">
          Preview is free and changes nothing. Generate and Re-check call the model.
        </p>

        {preview && (
          <div className="mb-10">
            <h2 className="text-xs font-semibold uppercase tracking-widest text-muted-foreground mb-3">
              {preview.page_count} pages · {preview.total_words?.toLocaleString()} words
            </h2>
            <div className="rounded-2xl border border-border overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-muted/50">
                  <tr className="text-left">
                    <th className="px-4 py-2 font-medium text-muted-foreground">Page</th>
                    <th className="px-4 py-2 font-medium text-muted-foreground">Topic</th>
                    <th className="px-4 py-2 font-medium text-muted-foreground text-right">
                      Questions
                    </th>
                    <th className="px-4 py-2 font-medium text-muted-foreground">Status</th>
                    <th className="px-4 py-2" />
                  </tr>
                </thead>
                <tbody>
                  {(preview.pages || []).map((p) => {
                    const total = p.existing_drafts + p.existing_published;
                    return (
                      <tr key={p.title} className="border-t border-border align-middle">
                        <td className="px-4 py-2.5 text-foreground">
                          {p.title}
                          <span className="ml-2 text-[10px] text-muted-foreground">
                            v{p.version} · {p.words}w
                          </span>
                        </td>
                        <td className="px-4 py-2.5 text-muted-foreground">
                          {topicLabel(p.topic)}
                          {p.topic_guessed && <span className="ml-1 text-[10px]">?</span>}
                        </td>
                        <td className="px-4 py-2.5 text-right tabular-nums text-muted-foreground">
                          {total || '—'}
                        </td>
                        <td className="px-4 py-2.5">
                          {p.too_short ? (
                            <span className="text-xs text-muted-foreground">too short</span>
                          ) : p.stale ? (
                            <span className="inline-flex items-center gap-1 text-xs text-destructive font-medium">
                              <AlertTriangle className="w-3 h-3" />
                              page changed
                            </span>
                          ) : total ? (
                            <span className="text-xs text-primary">current</span>
                          ) : (
                            <span className="text-xs text-muted-foreground">no questions</span>
                          )}
                        </td>
                        <td className="px-4 py-2.5 text-right whitespace-nowrap">
                          {confirmPage === p.title ? (
                            <span className="inline-flex items-center gap-2">
                              <span className="text-xs text-muted-foreground">
                                Archive {total}?
                              </span>
                              <button
                                type="button"
                                disabled={pageBusy !== null}
                                onClick={() => archivePage(p.title)}
                                className="text-xs font-medium text-destructive hover:underline disabled:opacity-40"
                              >
                                {pageBusy === `archive:${p.title}` ? 'Archiving…' : 'Yes'}
                              </button>
                              <button
                                type="button"
                                onClick={() => setConfirmPage(null)}
                                className="text-xs font-medium text-muted-foreground hover:text-foreground"
                              >
                                Cancel
                              </button>
                            </span>
                          ) : (
                            <>
                              {p.stale && total > 0 && (
                                <button
                                  type="button"
                                  disabled={pageBusy !== null}
                                  onClick={() => callFunction('recheck', [p.title])}
                                  className="text-xs font-medium text-primary hover:underline disabled:opacity-40 mr-3"
                                >
                                  {pageBusy === `recheck:${p.title}` ? 'Checking…' : 'Re-check'}
                                </button>
                              )}
                              {!p.too_short && (
                                <button
                                  type="button"
                                  disabled={pageBusy !== null}
                                  onClick={() => callFunction('generate', [p.title])}
                                  className="text-xs font-medium text-muted-foreground hover:text-foreground hover:underline disabled:opacity-40"
                                >
                                  {pageBusy === `generate:${p.title}`
                                    ? 'Working…'
                                    : total
                                    ? 'Regenerate'
                                    : 'Generate'}
                                </button>
                              )}
                              {total > 0 && (
                                <button
                                  type="button"
                                  title="Archive this page's questions"
                                  disabled={pageBusy !== null}
                                  onClick={() => setConfirmPage(p.title)}
                                  className="ml-3 align-middle text-muted-foreground hover:text-destructive disabled:opacity-40"
                                >
                                  <Archive className="w-3.5 h-3.5" />
                                </button>
                              )}
                            </>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {preview?.orphans?.length > 0 && (
          <div className="mb-10">
            <h2 className="text-xs font-semibold uppercase tracking-widest text-destructive mb-1">
              No longer labelled ({preview.orphans.length})
            </h2>
            <p className="text-xs text-muted-foreground mb-3">
              These pages have lost the{' '}
              <code className="text-[10px] px-1 rounded bg-muted">training</code> label in
              Confluence, but their questions are still being served to agents. Archive them if
              that was the intention.
            </p>
            <div className="rounded-2xl border border-destructive/30 overflow-hidden">
              <table className="w-full text-sm">
                <tbody>
                  {preview.orphans.map((o) => (
                    <tr key={o.title} className="border-b border-border last:border-0">
                      <td className="px-4 py-2.5 text-foreground">{o.title}</td>
                      <td className="px-4 py-2.5 text-right tabular-nums text-muted-foreground">
                        {o.existing_published + o.existing_drafts} questions
                      </td>
                      <td className="px-4 py-2.5 text-right whitespace-nowrap">
                        {confirmPage === o.title ? (
                          <span className="inline-flex items-center gap-2">
                            <button
                              type="button"
                              disabled={pageBusy !== null}
                              onClick={() => archivePage(o.title)}
                              className="text-xs font-medium text-destructive hover:underline disabled:opacity-40"
                            >
                              {pageBusy === `archive:${o.title}` ? 'Archiving…' : 'Yes, archive'}
                            </button>
                            <button
                              type="button"
                              onClick={() => setConfirmPage(null)}
                              className="text-xs font-medium text-muted-foreground hover:text-foreground"
                            >
                              Cancel
                            </button>
                          </span>
                        ) : (
                          <button
                            type="button"
                            disabled={pageBusy !== null}
                            onClick={() => setConfirmPage(o.title)}
                            className="inline-flex items-center gap-1.5 text-xs font-medium text-muted-foreground hover:text-destructive disabled:opacity-40"
                          >
                            <Archive className="w-3.5 h-3.5" />
                            Archive
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {flagged.length > 0 && (
          <div className="mb-10">
            <h2 className="text-xs font-semibold uppercase tracking-widest text-destructive mb-1">
              Needs attention ({flagged.length})
            </h2>
            <p className="text-xs text-muted-foreground mb-3">
              The source page changed and these no longer match it. They are hidden from agents
              until you decide.
            </p>
            <div className="space-y-4">
              {flagged.map((row) => (
                <QuestionCard
                  key={row.id}
                  row={row}
                  flagged
                  busy={busyId === row.id}
                  onKeep={keep}
                  onReject={retire}
                />
              ))}
            </div>
          </div>
        )}

        <h2 className="text-xs font-semibold uppercase tracking-widest text-muted-foreground mb-3">
          Awaiting review {drafts?.length ? `(${drafts.length})` : ''}
        </h2>

        {drafts === null ? (
          <div className="flex justify-center py-12">
            <div className="w-8 h-8 border-4 border-muted border-t-primary rounded-full animate-spin" />
          </div>
        ) : drafts.length === 0 ? (
          <div className="flex items-start gap-2.5 p-5 rounded-2xl border border-dashed border-border">
            <AlertCircle className="w-4 h-4 mt-0.5 text-muted-foreground flex-shrink-0" />
            <p className="text-sm text-muted-foreground">
              No drafts waiting. Label pages in Confluence with{' '}
              <code className="text-xs px-1 rounded bg-muted">training</code>, run Preview, then
              Generate.
            </p>
          </div>
        ) : (
          <div className="space-y-4">
            {drafts.map((row) => (
              <QuestionCard
                key={row.id}
                row={row}
                busy={busyId === row.id}
                onApprove={approve}
                onReject={retire}
              />
            ))}
          </div>
        )}
      </main>
    </div>
  );
}
